import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import EditIcon from "@mui/icons-material/Edit";
import LoginIcon from "@mui/icons-material/Login";
import { useNotify } from "react-admin";
import axios from "axios";
import { organizationsApi, userApi, API_BASE } from "../providers/env";
import { OrganizationStats } from "../pages/RolesAndPermissions/types";

interface OrganizationCardProps {
  stats: OrganizationStats;
  onClick?: () => void;
  onUpdated?: () => void;
}

export const OrganizationCard: React.FC<OrganizationCardProps> = ({
  stats,
  onClick,
  onUpdated,
}) => {
  const notify = useNotify();
  const { organization, sub_admin_count, user_count } = stats;
  const [openEdit, setOpenEdit] = useState(false);
  const [editName, setEditName] = useState(organization.name);
  const [saving, setSaving] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  const handleEditOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditName(organization.name);
    setOpenEdit(true);
  };

  const handleEditSave = async () => {
    if (!editName.trim()) {
      notify("Organization name is required", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      await organizationsApi.updateOrganization({
        orgId: organization.id,
        organizationUpdate: { name: editName.trim() },
      });
      notify("Organization updated successfully", { type: "success" });
      setOpenEdit(false);
      onUpdated?.();
    } catch (error: any) {
      notify(error?.response?.data?.detail || "Error updating organization", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleLoginToOrg = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoggingIn(true);
    try {
      const token = localStorage.getItem("token") || "";
      const response = await axios.post(
        `${API_BASE}/api/v1/auth/login-to-org`,
        { organization_id: organization.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;

      // Save current super admin token so we can switch back
      localStorage.setItem("super_admin_token", token);
      localStorage.setItem("super_admin_permissions", localStorage.getItem("permissions") || "");

      // Set new org admin token
      localStorage.setItem("token", data.access_token);
      localStorage.removeItem("permissions");
      localStorage.removeItem("organization_id");
      localStorage.removeItem("is_superuser");
      localStorage.removeItem("agentId");
      localStorage.removeItem("agentName");

      // Load identity/permissions for the org admin before redirecting
      try {
        const identityResp = await userApi.usersCurrentUser();
        const identity = identityResp?.data;
        if (identity) {
          localStorage.setItem("permissions", JSON.stringify(identity));
          if (identity.organization_id) {
            localStorage.setItem("organization_id", identity.organization_id);
          }
        }
      } catch (err) {
        console.error("Error loading org admin identity:", err);
      }

      notify(`Logged in to ${organization.name} as Org Admin`, { type: "success" });

      // Redirect to agents page (org admin landing)
      window.location.href = "/agents";
    } catch (error: any) {
      notify(error?.response?.data?.detail || "Error logging in to organization", { type: "error" });
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <>
    <Card
      variant="outlined"
      sx={{
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { boxShadow: 2 } : {},
        transition: "box-shadow 0.2s",
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
          <BusinessIcon sx={{ fontSize: 32, color: "text.secondary" }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              {organization.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Created {new Date(organization.created_at).toLocaleDateString()}
            </Typography>
          </Box>
          <Tooltip title="Edit Organization">
            <IconButton
              size="small"
              onClick={handleEditOpen}
              sx={{ "&:hover": { backgroundColor: "#f0f0f0" } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Login to Organization">
            <IconButton
              size="small"
              onClick={handleLoginToOrg}
              disabled={loggingIn}
              sx={{
                color: "#1976d2",
                "&:hover": { backgroundColor: "#e3f2fd" },
              }}
            >
              {loggingIn ? (
                <CircularProgress size={18} />
              ) : (
                <LoginIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Box
            sx={{
              flex: "1 1 45%",
              backgroundColor: "#F3F4F6",
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" color="primary">
              {sub_admin_count}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sub Admins
            </Typography>
          </Box>

          <Box
            sx={{
              flex: "1 1 45%",
              backgroundColor: "#F3F4F6",
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <Typography variant="h6">{user_count}</Typography>
            <Typography variant="body2" color="text.secondary">
              Users
            </Typography>
          </Box>

          <Box
            sx={{
              flex: "1 1 100%",
              backgroundColor: "#ECFDF5",
              p: 1.5,
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" color="success.main">
              {organization.is_active ? "Active" : "Inactive"}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>

    <Dialog
      open={openEdit}
      onClose={() => !saving && setOpenEdit(false)}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle>Edit Organization</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Organization Name"
          fullWidth
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          disabled={saving}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => setOpenEdit(false)} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleEditSave}
          variant="contained"
          disabled={saving || !editName.trim()}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            backgroundColor: "#1a1a1a",
            "&:hover": { backgroundColor: "#333333" },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};
